import * as R from 'ramda';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import React, { useRef, useState, useEffect } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// components
import { MuiTextInputForDatePicker } from '../../mui-text-input';
import { TimePickerMui } from '../../timepicker-mui';
// helpers
import * as G from '../../../helpers';
// ui
import { Box, Flex, ModalActionButton, ModalCancelButton } from '../../../ui';
//////////////////////////////////////////////////

const getTimeValue = (value) => (
  G.isNilOrEmpty(value)
    ? null
    : R.compose(R.join(' '), R.takeLast(2), R.split(' '))(value)
);

const getRangeDisplayValue = ({ to, from }) => {
  if (R.all(G.isNilOrEmpty, [from, to])) return '';

  const fromText = G.isNilOrEmpty(from)
    ? G.getWindowLocale('titles:from', 'From')
    : G.convertTimeToConfigFormat(from);
  const toText = G.isNilOrEmpty(to)
    ? G.getWindowLocale('titles:to', 'To')
    : G.convertTimeToConfigFormat(to);

  return `${fromText} - ${toText}`;
};

const TimeRangeTitleFilter = (props) => {
  const {
    title,
    width,
    onApply,
    titleFilterValue,
  } = props;

  const anchorRef = useRef(null);

  const fromValue = getTimeValue(R.path(['from'], titleFilterValue));
  const toValue = getTimeValue(R.path(['to'], titleFilterValue));
  const currentRange = { to: toValue, from: fromValue };

  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(currentRange);
  const [appliedRange, setAppliedRange] = useState(currentRange);

  const hasDraftValue = R.any(G.isNotNilAndNotEmpty, R.values(draftRange));

  useEffect(() => {
    setAppliedRange(currentRange);

    if (G.isFalse(isOpen)) setDraftRange(currentRange);
  }, [toValue, fromValue]);

  const handleOpen = () => {
    setDraftRange(appliedRange);
    setIsOpen(true);
  };

  const handleClose = () => {
    setDraftRange(appliedRange);
    setIsOpen(false);
  };

  const handleApply = () => {
    setAppliedRange(draftRange);
    setIsOpen(false);
    onApply(draftRange);
  };

  const handleClear = () => {
    const emptyRange = { to: null, from: null };

    setDraftRange(emptyRange);
    setAppliedRange(emptyRange);
    setIsOpen(false);
    onApply(emptyRange);
  };

  const placeholder = `${G.getWindowLocale('titles:from', 'From')} - ${
    G.getWindowLocale('titles:to', 'To')
  }`;

  return (
    <Box ref={anchorRef} width={width} data-testid='time-range-title-filter'>
      <MuiTextInputForDatePicker
        label={title}
        readOnly={true}
        onClick={handleOpen}
        isFocused={isOpen}
        inputReadOnly={true}
        placeholder={placeholder}
        value={getRangeDisplayValue(appliedRange)}
        inputStyles={{ width, height: 32, maxWidth: width }}
      />
      <Popper
        open={isOpen}
        sx={{ zIndex: 1300 }}
        placement='bottom-start'
        anchorEl={anchorRef.current}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={6}>
            <Flex p='12px' gap='8px' alignItems='flex-start'>
              <TimePickerMui
                width={150}
                isClearable={false}
                disablePortal={true}
                inputReadOnly={false}
                value={R.prop('from', draftRange)}
                ariaLabel={G.getWindowLocale('titles:from', 'From')}
                placeholder={G.getWindowLocale('titles:from', 'From')}
                onChange={(from) => setDraftRange(R.assoc('from', from, draftRange))}
                inputSettings={{
                  '.MuiOutlinedInput-input': {
                    fontSize: 12,
                    padding: '5px 8px',
                    textAlign: 'center',
                  },
                }}
              />
              <TimePickerMui
                width={150}
                isClearable={false}
                disablePortal={true}
                inputReadOnly={false}
                value={R.prop('to', draftRange)}
                ariaLabel={G.getWindowLocale('titles:to', 'To')}
                placeholder={G.getWindowLocale('titles:to', 'To')}
                onChange={(to) => setDraftRange(R.assoc('to', to, draftRange))}
                inputSettings={{
                  '.MuiOutlinedInput-input': {
                    fontSize: 12,
                    padding: '5px 8px',
                    textAlign: 'center',
                  },
                }}
              />
            </Flex>
            <Flex p='0 12px 12px' gap='8px' justifyContent='flex-end'>
              {
                hasDraftValue &&
                <ModalCancelButton type='button' onClick={handleClear}>
                  {G.getWindowLocale('actions:clear', 'Clear')}
                </ModalCancelButton>
              }
              <ModalActionButton type='button' onClick={handleApply}>
                {G.getWindowLocale('actions:apply', 'Apply')}
              </ModalActionButton>
            </Flex>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default TimeRangeTitleFilter;
